import { useState } from 'react'
import type { RefObject } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import { useQueryClient } from '@tanstack/react-query'
import { bulkUpdateMatrix } from '../../../shared/api/requestItems.api'

/**
 * Хук массовой установки матрицы. Работает с тем же `selectedIds`-состоянием,
 * что и удаление (useDeleteSelectedItems) — поэтому после успешного применения
 * сбрасывает выделение через gridRef.
 */
export function useBulkUpdateMatrix(
  requestId: string | undefined,
  gridRef: RefObject<AgGridReact | null>,
  onClearSelection: () => void,
) {
  const queryClient = useQueryClient()
  const [isApplying, setIsApplying] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function apply(itemIds: string[], matrix: string | null) {
    if (!requestId || itemIds.length === 0 || isApplying) return
    setIsApplying(true)
    setError(null)
    try {
      await bulkUpdateMatrix(requestId, itemIds, matrix)
      onClearSelection()
      gridRef.current?.api.deselectAll()
      await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось применить матрицу'
      setError(msg)
    } finally {
      setIsApplying(false)
    }
  }

  return { isApplying, error, apply, clearError: () => setError(null) }
}
